import React, { useState } from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { cv } from '../themeVars';

// A guest who missed their tour chose a new slot through the AI (backend
// demo.lib.missed_tour_rebooking). Phase 1: staff create the rebooking and the
// payment link in their own booking system, then Approve here -- which puts a
// payment-link message in the composer -- or Decline. Nothing is charged or
// moved automatically. The data arrives over conversation_info, never in Twilio
// message attributes (those reach the guest).
const styles = {
  root: {
    margin: '6px 12px 8px 12px',
    padding: '10px 14px',
    border: `1px solid ${cv('warn-border', '#f0c36d')}`,
    borderRadius: 10,
    background: cv('warn-tint', '#fffaf0'),
    fontSize: '0.85rem',
    lineHeight: 1.45,
    color: cv('text', '#3d3524'),
  },
  headline: { fontWeight: 700, color: cv('warn-text', '#8a5a00'), marginBottom: 4 },
  row: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  label: { color: cv('text-secondary', '#7a6a4a'), minWidth: 72 },
  warn: { color: cv('danger-text', '#a8201a'), fontSize: '0.78rem', marginTop: 2 },
  actions: { display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' },
  fields: { display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' },
};

function formatMissed(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function RebookingRequestCard({ request, onApprove, onDecline, busy }) {
  const [editing, setEditing] = useState(false);
  const [slot, setSlot] = useState(request.chosen_slot_label || '');
  const [fee, setFee] = useState(request.fee_total || '');
  const party = request.pax === 1 ? '1 person' : `${request.pax} people`;
  const missed = formatMissed(request.missed_start_ts);

  const approve = () => {
    if (editing) {
      onApprove({ chosen_slot_label: slot.trim(), fee_total: String(fee).trim() });
    } else {
      onApprove({});
    }
  };

  return (
    <div style={styles.root} data-testid="rebooking-request-card">
      <div style={styles.headline}>Rebooking request</div>
      <div style={styles.row}>
        <span style={styles.label}>Missed</span>
        <span>{request.tour_name}{missed ? `, ${missed}` : ''}</span>
      </div>
      <div style={styles.row}>
        <span style={styles.label}>Wants</span>
        <span>{request.chosen_slot_label}</span>
      </div>
      {!request.chosen_slot_confirmed_available ? (
        <div style={styles.warn}>The guest named this time themselves: check availability before approving.</div>
      ) : null}
      <div style={styles.row}>
        <span style={styles.label}>Fee</span>
        <span>{request.fee_total_display} ({party}, per your policy)</span>
      </div>
      <div style={styles.row}>
        <span style={styles.label}>Email</span>
        <span>{request.guest_email}</span>
      </div>
      {request.booking_ref ? (
        <div style={styles.row}>
          <span style={styles.label}>Booking</span>
          <span>{request.booking_ref}</span>
        </div>
      ) : null}
      {editing ? (
        <div style={styles.fields}>
          <TextField size="small" label="New time" value={slot} onChange={(e) => setSlot(e.target.value)}
                     inputProps={{ 'data-testid': 'rebooking-slot' }} />
          <TextField size="small" label={`Fee (${request.currency})`} value={fee} onChange={(e) => setFee(e.target.value)}
                     inputProps={{ 'data-testid': 'rebooking-fee', inputMode: 'decimal' }} />
        </div>
      ) : null}
      <div style={styles.actions}>
        <Button size="small" variant="contained" color="primary" onClick={approve} disabled={!!busy}>
          Approve
        </Button>
        {!editing ? (
          <Button size="small" variant="outlined" onClick={() => setEditing(true)} disabled={!!busy}>
            Change
          </Button>
        ) : null}
        <Button size="small" variant="text" color="inherit" onClick={onDecline} disabled={!!busy}>
          Decline
        </Button>
      </div>
    </div>
  );
}
